import { useLocation, Link } from 'react-router-dom';

import { Avatar, Box, ButtonBase, Popover, Tooltip, Typography } from '@mui/material';
import { alpha } from '@mui/material/styles';

import { Check, CircleUserRound, LogOut } from 'lucide-react';

import AppVersion from '../AppVersion';
import { useUserStateContext } from '../UserStateProvider';
import useSignOut from '@/features/authentication/hooks/useSignOut';
import { getFirstLettersOfName, getRandomPastelColor } from '@/shared/services';
import { cx } from '@/styles/muiTheme';

import type { MenuItem, MenuSection } from './NavigationMenu';

interface MainMenuPanelProps {
  anchorEl: HTMLElement | null;
  onClose: () => void;
  sections: MenuSection[];
  userEmail: string;
  avatarBgColor: string;
}

// Which sections share a column, top to bottom. Sections are matched by title,
// so one that is not listed here does not appear in this menu.
const NAV_COLUMNS = [
  ['Watch', 'Help & Support'],
  ['Manage Organization', 'MCP', 'Clip Export API'],
  ['Manage CXVH'],
];

// The design is 1140px for all four columns: 1.15 units for the account column
// plus one per nav column. Columns the user has no items in are dropped, so the
// panel narrows with them instead of leaving an empty column. Below md the
// columns stack into one, which the popover scrolls.
const COLUMN_WIDTH = 275;

const labelSx = {
  fontSize: 11,
  lineHeight: '14px',
  letterSpacing: '0.15px',
  color: cx.text.tertiary,
} as const;

const itemSx = {
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'flex-start',
  gap: '10px',
  width: '100%',
  padding: '6px 8px',
  borderRadius: '4px',
  color: cx.text.primary,
  fontSize: 12,
  letterSpacing: '0.15px',
  textAlign: 'left',
  '&:hover': { backgroundColor: cx.bg.elevated },
  '&.Mui-focusVisible': { boxShadow: `0 0 0 2px ${cx.blue.main}` },
} as const;

// Section icons arrive as prebuilt lucide elements sized for the vertical
// menu; CSS stroke-width outranks their stroke-width attribute.
const iconSx = {
  display: 'flex',
  '& svg': { width: 16, height: 16, strokeWidth: 1.5, color: cx.text.tertiary },
} as const;

const MainMenuPanel = ({
  anchorEl,
  onClose,
  sections,
  userEmail,
  avatarBgColor,
}: MainMenuPanelProps) => {
  const location = useLocation();
  const {
    hasRole,
    userProfile,
    currentOrg,
    currentLeague,
    availableOrgs,
    availableLeagues,
    actions: { updateDefaultOrg, updateDefaultLeague },
  } = useUserStateContext();
  const signOut = useSignOut();

  const visibleSections = sections
    .map((section) => ({
      ...section,
      items: section.items.filter((item) => hasRole(item.roles ?? [])),
    }))
    .filter((section) => section.items.length > 0);
  const columns = NAV_COLUMNS.map((titles) =>
    titles.flatMap((title) => visibleSections.filter((section) => section.title === title)),
  ).filter((column) => column.length > 0);

  const renderItem = ({
    path,
    icon,
    label,
    external,
    href,
    onClick,
    disabled,
    disabledTooltip,
  }: MenuItem) => {
    const content = (
      <>
        <Box component="span" sx={iconSx}>
          {icon}
        </Box>
        {label}
      </>
    );

    if (disabled) {
      // The span is load-bearing: a disabled button gets no pointer events, so
      // a Tooltip anchored straight to it never fires.
      return (
        <Tooltip key={path} title={disabledTooltip ?? ''} placement="right">
          <Box component="span" sx={{ display: 'block' }}>
            <ButtonBase disabled sx={{ ...itemSx, opacity: 0.38 }}>
              {content}
            </ButtonBase>
          </Box>
        </Tooltip>
      );
    }

    if (external) {
      return (
        <ButtonBase
          key={path}
          component="a"
          href={href}
          target="_blank"
          rel="noopener noreferrer"
          onClick={onClose}
          sx={itemSx}
        >
          {content}
        </ButtonBase>
      );
    }

    if (onClick) {
      return (
        <ButtonBase
          key={path}
          onClick={() => {
            onClick();
            onClose();
          }}
          sx={itemSx}
        >
          {content}
        </ButtonBase>
      );
    }

    return (
      <ButtonBase
        key={path}
        component={Link}
        to={path}
        onClick={onClose}
        sx={{ ...itemSx, ...(location.pathname === path && { backgroundColor: cx.bg.elevated }) }}
      >
        {content}
      </ButtonBase>
    );
  };

  return (
    <Popover
      open={Boolean(anchorEl)}
      anchorEl={anchorEl}
      onClose={onClose}
      anchorOrigin={{ vertical: 'bottom', horizontal: 'left' }}
      transformOrigin={{ vertical: 'top', horizontal: 'left' }}
      // Popover's Grow scales the panel out of its top-left corner, under the
      // menu button. Fixed timings: 'auto' derives them from the panel height,
      // which comes to ~350ms here and feels slow for a menu.
      transitionDuration={{ enter: 200, exit: 120 }}
      slotProps={{
        paper: {
          sx: {
            // anchorEl is the menu IconButton, inset 8px inside its AppBar tile.
            // Step out of that inset so the panel lines up with the tile's left
            // edge and leaves the AppBar's own 8px gutter below it.
            mt: 2,
            ml: -1,
            width: { xs: 'calc(100% - 32px)', md: (1.15 + columns.length) * COLUMN_WIDTH },
            // Glass: a translucent fill over a blurred copy of the page behind it.
            backgroundColor: alpha(cx.bg.surface, 0.72),
            backdropFilter: 'blur(16px)',
            WebkitBackdropFilter: 'blur(16px)', // For Safari
            border: `1px solid ${cx.border.subtle}`,
            borderRadius: '6px',
          },
        },
      }}
    >
      <Box
        sx={{
          display: 'grid',
          gridTemplateColumns: {
            xs: '1fr',
            md: `minmax(0, 1.15fr) repeat(${columns.length}, minmax(0, 1fr))`,
          },
          '& > *:not(:last-child)': {
            borderBottom: { xs: `1px solid ${cx.border.subtle}`, md: 'none' },
            borderRight: { xs: 'none', md: `1px solid ${cx.border.subtle}` },
          },
        }}
      >
        {/* Account, organization and league */}
        <Box
          sx={{
            padding: 2,
            display: 'flex',
            flexDirection: 'column',
            gap: '12px',
            backgroundColor: alpha(cx.bg.surface2, 0.5),
          }}
        >
          <Box sx={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
            <Avatar
              src={userProfile?.profilePicturePath ?? undefined}
              sx={{ bgcolor: avatarBgColor, width: 32, height: 32, fontSize: 12 }}
            >
              {getFirstLettersOfName(userEmail)}
            </Avatar>
            <Typography noWrap sx={{ fontSize: 12, lineHeight: '16px', letterSpacing: '0.15px' }}>
              {userEmail}
            </Typography>
          </Box>

          {availableOrgs.length > 1 && (
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: '4px' }}>
              <Typography sx={{ ...labelSx, paddingBottom: '2px' }}>Organization</Typography>
              {availableOrgs.map((org) => {
                const selected = org.id === currentOrg?.id;
                return (
                  <ButtonBase
                    key={org.id}
                    onClick={() => {
                      if (selected) return;
                      updateDefaultOrg(org);
                      onClose();
                    }}
                    sx={{
                      ...itemSx,
                      border: `1px solid ${selected ? cx.border.default : 'transparent'}`,
                      backgroundColor: selected ? cx.bg.elevated : 'transparent',
                    }}
                  >
                    <Avatar
                      src={org.logoPath ?? undefined}
                      variant="rounded"
                      sx={{
                        width: 28,
                        height: 28,
                        borderRadius: '4px',
                        fontSize: 11,
                        bgcolor: org.logoPath
                          ? 'transparent'
                          : getRandomPastelColor(org.name[0], org.name[2]),
                      }}
                    >
                      {getFirstLettersOfName(org.name)}
                    </Avatar>
                    <Box component="span" sx={{ flex: 1 }}>
                      {org.name}
                    </Box>
                    {selected && <Check size={14} strokeWidth={2} />}
                  </ButtonBase>
                );
              })}
            </Box>
          )}

          {availableLeagues.length > 1 && (
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: '6px' }}>
              <Typography sx={labelSx}>League</Typography>
              <Box
                sx={{
                  display: 'grid',
                  gridTemplateColumns: `repeat(${availableLeagues.length}, 1fr)`,
                  gap: '2px',
                  padding: '2px',
                  borderRadius: '100px',
                  backgroundColor: cx.bg.surface3,
                }}
              >
                {availableLeagues.map((league) => {
                  const selected = league.id === currentLeague?.id;
                  return (
                    <ButtonBase
                      key={league.id}
                      onClick={() => {
                        if (selected) return;
                        updateDefaultLeague(league);
                        onClose();
                      }}
                      sx={{
                        padding: '6px 8px',
                        borderRadius: '100px',
                        fontSize: 11,
                        fontWeight: 500,
                        letterSpacing: '0.15px',
                        backgroundColor: selected ? cx.light[200] : 'transparent',
                        color: selected ? cx.text.onLight : cx.text.secondary,
                        '&.Mui-focusVisible': { boxShadow: `0 0 0 2px ${cx.blue.main}` },
                      }}
                    >
                      {league.displayName}
                    </ButtonBase>
                  );
                })}
              </Box>
            </Box>
          )}
        </Box>

        {columns.map((column) => (
          <Box
            key={column[0].title}
            sx={{ padding: '12px 8px', display: 'flex', flexDirection: 'column', gap: '12px' }}
          >
            {column.map((section) => (
              <Box
                key={section.title}
                sx={{ display: 'flex', flexDirection: 'column', gap: '1px' }}
              >
                <Typography sx={{ ...labelSx, padding: '4px 8px' }}>{section.title}</Typography>
                {section.items.map(renderItem)}
              </Box>
            ))}
          </Box>
        ))}
      </Box>

      {/* Version, account settings and sign out */}
      <Box
        sx={{
          display: 'flex',
          flexWrap: 'wrap',
          alignItems: 'center',
          gap: '4px',
          padding: '4px 8px 4px 16px',
          borderTop: `1px solid ${cx.border.subtle}`,
        }}
      >
        <AppVersion
          sx={{
            flex: 1,
            opacity: 1,
            fontFamily: cx.fontMono,
            fontSize: 10,
            lineHeight: '14px',
            color: cx.text.muted,
          }}
        />
        <ButtonBase
          component={Link}
          to="/account-settings"
          onClick={onClose}
          sx={{ ...itemSx, width: 'auto', gap: '8px', padding: '6px 10px' }}
        >
          <Box component="span" sx={iconSx}>
            <CircleUserRound />
          </Box>
          Account Settings & Preferences
        </ButtonBase>
        <ButtonBase
          onClick={signOut}
          sx={{ ...itemSx, width: 'auto', gap: '8px', padding: '6px 10px' }}
        >
          <Box component="span" sx={iconSx}>
            <LogOut />
          </Box>
          Sign Out
        </ButtonBase>
      </Box>
    </Popover>
  );
};

export default MainMenuPanel;
